// Role-based API protection utilities

export interface ApiEndpointConfig {
  endpoint: string
  allowedRoles: string[]
  requiresPermissions?: string[]
}

// Define role-based API access rules
export const API_ACCESS_RULES: ApiEndpointConfig[] = [
  // Internal-only endpoints
  {
    endpoint: "/client/",
    allowedRoles: ["internal", "client"],
  },
  {
    endpoint: "/org/",
    allowedRoles: ["internal"],
  },
  {
    endpoint: "/social_media/",
    allowedRoles: ["internal"],
  },
  {
    endpoint: "/address/",
    allowedRoles: ["internal"],
  },
  {
    endpoint: "/client_order/",
    allowedRoles: ["internal", "client"],
  },
  {
    endpoint: "/client_order_connection/",
    allowedRoles: ["internal", "client"],
  },
  {
    endpoint: "/anchor_text/",
    allowedRoles: ["internal", "client"],
  },
  {
    endpoint: "/url/",
    allowedRoles: ["internal", "client"],
  },
  {
    endpoint: "/combo/",
    allowedRoles: ["internal", "client"],
  },
  {
    endpoint: "/opp/",
    allowedRoles: ["internal", "client"],
  },
  {
    endpoint: "/location/",
    allowedRoles: ["internal"],
  },
  {
    endpoint: "/event_type/",
    allowedRoles: ["internal"],
  },
  {
    endpoint: "/benefit/",
    allowedRoles: ["internal"],
  },
  {
    endpoint: "/sponsorship_level/",
    allowedRoles: ["internal"],
  },
  {
    endpoint: "/organizations/",
    allowedRoles: ["internal"],
  },
  {
    endpoint: "/opportunities/",
    allowedRoles: ["internal"],
  },
  {
    endpoint: "/tasks/",
    allowedRoles: ["internal"],
  },

  // Organization-specific endpoints
  {
    endpoint: "/organization/opportunities/",
    allowedRoles: ["organization"],
  },
  {
    endpoint: "/organization/campaigns/",
    allowedRoles: ["organization"],
  },
  {
    endpoint: "/organization/performance/",
    allowedRoles: ["organization"],
  },
  {
    endpoint: "/organization/profile/",
    allowedRoles: ["organization"],
  },

  // Client-specific endpoints
  {
    endpoint: "/client/orders/",
    allowedRoles: ["client"],
  },
  {
    endpoint: "/client/reports/",
    allowedRoles: ["client"],
  },
  {
    endpoint: "/client/billing/",
    allowedRoles: ["client"],
  },
  {
    endpoint: "/client/messages/",
    allowedRoles: ["client"],
  },

  // Shared endpoints (accessible by multiple roles)
  {
    endpoint: "/auth/token/",
    allowedRoles: ["internal", "organization", "client"], // Login endpoint
  },
  {
    endpoint: "/auth/refresh/",
    allowedRoles: ["internal", "organization", "client"], // Token refresh
  },
  {
    endpoint: "/user/register",
    allowedRoles: ["internal", "organization", "client"],
  },
  {
    endpoint: "/user/",
    allowedRoles: ["internal", "super_admin", "matchmaker", "order_manager"],
  },
]

export function isEndpointAllowed(endpoint: string, userRole: string): boolean {
  // Find matching rule for the endpoint
  const rule = API_ACCESS_RULES.find((rule) => endpoint.includes(rule.endpoint))

  if (!rule) {
    // If no specific rule found, default to internal-only access
    console.warn(`⚠️ [v0] No API access rule found for endpoint: ${endpoint}, defaulting to internal-only`)
    return userRole === "internal"
  }

  const isAllowed = rule.allowedRoles.includes(userRole)
  console.log(`🔍 [v0] API access check: ${endpoint} for role '${userRole}' - ${isAllowed ? "ALLOWED" : "DENIED"}`)

  return isAllowed
}

export function checkApiPermissions(
  endpoint: string,
  userRole: string,
  userPermissions: string[] = [],
): {
  allowed: boolean
  reason?: string
} {
  const rule = API_ACCESS_RULES.find((rule) => endpoint.includes(rule.endpoint))

  if (!rule) {
    return {
      allowed: userRole === "internal",
      reason: userRole !== "internal" ? "No access rule defined for this endpoint" : undefined,
    }
  }

  // Check role access
  if (!rule.allowedRoles.includes(userRole)) {
    return {
      allowed: false,
      reason: `Role '${userRole}' not allowed. Required roles: ${rule.allowedRoles.join(", ")}`,
    }
  }

  // Check permissions if required
  if (rule.requiresPermissions && rule.requiresPermissions.length > 0) {
    const hasRequiredPermissions = rule.requiresPermissions.every((permission) => userPermissions.includes(permission))

    if (!hasRequiredPermissions) {
      return {
        allowed: false,
        reason: `Missing required permissions: ${rule.requiresPermissions.join(", ")}`,
      }
    }
  }

  return { allowed: true }
}

export class ApiAccessError extends Error {
  constructor(
    message: string,
    public endpoint: string,
    public userRole: string,
    public requiredRoles: string[],
  ) {
    super(message)
    this.name = "ApiAccessError"
  }
}
